import { Button } from '@/components/design-system/Button';
import { ProductCard } from '@/components/screens/ShopPage/components';
import { FormulaProduct } from '@viome/viome-base/src/usecase/Shop/ShopQuery';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import OfferBannerWithTimer from '@screens/ShopPage/components/OfferBannerWithTimer';
import { StrikethroughPrice } from './StrikethroughPrice';
import { PriceDiscountSheet } from './PriceDiscountSheet';
import { LatestResultsBanner } from '@/components/shared/LatestResultsBanner';
import { RadioGroup } from '@/components/design-system/radio-group';

export type PriceInfo = {
  greenDaily: string;
  displayMonthlyPrice: string;
  strikethroughDaily: string | null;
  isPriceAvailable: boolean;
  hasDiscount: boolean;
  monthlyPriceWithOutDiscount: string;
  offerDuration?: string;
};

// Use the selected monthly total for both amounts; daily is its 30-day equivalent.
export const FormulaPriceSummary = ({
  priceInfo,
}: {
  priceInfo: PriceInfo;
}) => {
  if (!priceInfo.isPriceAvailable) return null;
  return (
    <div
      role="group"
      aria-label="Selected formulas price"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm leading-5"
    >
      <span className="text-xl leading-6 font-medium text-[#146464] whitespace-nowrap">
        ${priceInfo.greenDaily}/day
      </span>
      <span className="text-tertiary whitespace-nowrap">
        ${priceInfo.displayMonthlyPrice}/mo
      </span>
      {priceInfo.hasDiscount && (
        <StrikethroughPrice>
          ${priceInfo.monthlyPriceWithOutDiscount}
        </StrikethroughPrice>
      )}
      <PriceDiscountSheet priceInfo={priceInfo} />
    </div>
  );
};

const SelectedFormulaTotal = ({
  priceInfo,
  count,
}: {
  priceInfo: PriceInfo;
  count: number;
}) =>
  count > 0 && priceInfo.isPriceAvailable ? (
    <div className="flex w-full items-center justify-between gap-2 md:hidden">
      <span className="text-tertiary text-xs">
        {count} {count === 1 ? 'formula' : 'formulas'} selected
      </span>
      <span className="text-xl font-medium text-[#146464]">
        ${priceInfo.displayMonthlyPrice}/mo
      </span>
    </div>
  ) : null;

interface CustomizeModeViewProps {
  handleBackClick: () => void;
  nonSubscribedProducts: FormulaProduct[];
  selectedProductIds: Set<string>;
  handleToggleProductSelected: (productId: string) => void;
  handleSelectAll: () => void;
  dynamicPricing: PriceInfo;
  ctaButtonLabel: string;
  onCtaClick: () => void;
  isCtaLoading: boolean;
}

export const CustomizeModeView = ({
  handleBackClick,
  nonSubscribedProducts,
  selectedProductIds,
  handleToggleProductSelected,
  handleSelectAll,
  dynamicPricing,
  ctaButtonLabel,
  onCtaClick,
  isCtaLoading,
}: CustomizeModeViewProps) => {
  const savings =
    dynamicPricing?.hasDiscount &&
    dynamicPricing.strikethroughDaily &&
    dynamicPricing.greenDaily
      ? parseFloat(dynamicPricing.strikethroughDaily) -
        parseFloat(dynamicPricing.greenDaily)
      : 0;
  return (
    <div className="container mx-auto p-4 pb-20 md:p-8">
      <Button
        variant="ghost"
        onClick={handleBackClick}
        className="mb-6 text-primary hover:text-primary-focus"
      >
        <ChevronLeft className="w-5 h-5 mr-2" />
        Back
      </Button>
      <div className="flex flex-col md:flex-row gap-8 lg:gap-16">
        <div className="md:w-7/12 lg:w-8/12 space-y-6">
          <h1 className="typography-h1 text-primary mb-2">
            Choose your products
          </h1>
          <div className="space-y-4">
            {nonSubscribedProducts?.map((formula: FormulaProduct) => (
              <ProductCard
                key={formula.product_id}
                formula={formula}
                isSelected={selectedProductIds.has(String(formula.product_id))}
                onToggleSelected={handleToggleProductSelected}
                isEditable={true}
                cardStyle="default"
              />
            ))}
          </div>
          {nonSubscribedProducts.length > 0 && (
            <Button
              variant="link"
              onClick={handleSelectAll}
              className="text-success hover:text-[#027B63] p-0 typography-body-sm w-full justify-end pr-5"
            >
              {savings > 0
                ? `Select All and Save $${savings.toFixed(2)}/day`
                : 'Select All'}
            </Button>
          )}
        </div>
        <div className="md:w-5/12 lg:w-4/12 space-y-6">
          <div className="flex items-baseline gap-2 flex-wrap">
            <FormulaPriceSummary priceInfo={dynamicPricing} />
          </div>
          <div className="fixed bottom-16 md:bottom-auto md:relative md:left-auto left-0 w-full flex justify-center md:block z-50">
            <Button
              className="w-full rounded-md py-3 bg-black text-white hover:bg-opacity-90 typography-btn-medium md:min-w-0 min-w-[300px] px-24 md:px-5"
              onClick={onCtaClick}
              disabled={isCtaLoading || !dynamicPricing.isPriceAvailable}
            >
              {isCtaLoading ? 'Processing...' : ctaButtonLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface AllSubscribedViewProps {
  products: FormulaProduct[];
}

export const AllSubscribedView = ({ products }: AllSubscribedViewProps) => {
  return (
    <>
      <div className="flex justify-center">
        <LatestResultsBanner className="mb-6" />
      </div>

      <div className="container flex flex-col md:flex-row gap-8 p-4">
        <div className="md:w-5/12 flex flex-col">
          <h1 className="typography-h1 md:typography-display2 text-primary mb-2 text-center md:text-start">
            You are taking full advantage of our great products.
          </h1>
        </div>
        <div className="max-w-md mx-auto md:w-7/12">
          <h2 className="typography-h3 text-primary font-medium mb-4">
            Formulas in Your Plan
          </h2>
          {products.map((formula: FormulaProduct) => (
            <ProductCard
              key={formula.product_id}
              formula={formula}
              isSelected={true}
              isEditable={false}
              cardStyle="subscribedHighlight"
            />
          ))}
        </div>
      </div>
    </>
  );
};

interface AnySubscribedViewProps {
  selectedFormulaCount: number;
  pricingForUpgrade: PriceInfo;
  ctaButtonLabel: string;
  nonSubscribedProducts: FormulaProduct[];
  products: FormulaProduct[];
  onCtaClick: () => void;
  isCtaLoading: boolean;
  isComponentizationUser: boolean;
  isMultiFormulaSelectUser: boolean;
  selectedProductIds: Set<string>;
  handleSelectSingleProduct: (productId: string) => void;
  handleToggleProductSelected: (productId: string) => void;
  dynamicPricing: PriceInfo;
  discountPercentage?: number;
}

export const AnySubscribedView = ({
  selectedFormulaCount,
  pricingForUpgrade,
  ctaButtonLabel,
  nonSubscribedProducts,
  products,
  onCtaClick,
  isCtaLoading,
  isComponentizationUser,
  isMultiFormulaSelectUser,
  selectedProductIds,
  handleSelectSingleProduct,
  handleToggleProductSelected,
  dynamicPricing,
  discountPercentage,
}: AnySubscribedViewProps) => {
  const subscribedProducts = products.filter((p) => p.is_subscribed);
  const priceInfoToShow = isComponentizationUser
    ? dynamicPricing
    : pricingForUpgrade;

  return (
    <>
      <div className="flex justify-center">
        <LatestResultsBanner className="mb-6" />
      </div>

      <div className="container mx-auto max-w-5xl flex flex-col md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] gap-4 md:gap-8 p-4 pb-20 md:pb-4">
        <div className="contents md:block md:min-w-0">
          <div className="order-1">
            <h1 className="typography-h1 text-primary mb-2">
              Maximize the benefits of your nutrition plan
            </h1>
            {priceInfoToShow.isPriceAvailable && (
              <>
                <p className="typography-body text-secondary mb-3">
                  Upgrade for an additional
                </p>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <FormulaPriceSummary priceInfo={priceInfoToShow} />
                </div>
              </>
            )}
          </div>
          <div
            className={`sticky bottom-16 order-3 -mx-4 flex flex-wrap items-center gap-3 md:static md:mx-0 md:block z-50 px-4 md:px-0 ${
              isMultiFormulaSelectUser
                ? 'bg-white border-t border-gray-200 py-3 md:border-0 md:bg-transparent md:py-0'
                : ''
            }`}
          >
            {isMultiFormulaSelectUser && (
              <SelectedFormulaTotal
                priceInfo={priceInfoToShow}
                count={selectedFormulaCount}
              />
            )}
            <Button
              className={`min-h-12 flex-1 rounded-md py-3 bg-black text-white hover:bg-opacity-90 typography-btn-medium min-w-[140px] md:min-w-0 px-4 md:px-5 ${
                isMultiFormulaSelectUser ? 'mt-0 md:mt-4' : 'mt-4'
              }`}
              onClick={onCtaClick}
              disabled={
                isCtaLoading ||
                (isComponentizationUser && !dynamicPricing.isPriceAvailable)
              }
            >
              {isCtaLoading ? 'Processing...' : ctaButtonLabel}
            </Button>
          </div>
        </div>
        <div className="order-2 w-full min-w-0">
          {isComponentizationUser && subscribedProducts.length > 0 && (
            <>
              <h2 className="typography-h3 text-primary font-medium mb-4">
                Your Current Subscriptions
              </h2>
              {subscribedProducts.map((formula: FormulaProduct) => (
                <ProductCard
                  key={formula.product_id}
                  formula={formula}
                  isSelected={true}
                  isEditable={true}
                  disabled={true}
                  cardStyle="subscribedHighlight"
                  showPrice={isComponentizationUser}
                  discountPercentage={discountPercentage}
                />
              ))}
            </>
          )}
          {nonSubscribedProducts.length > 0 && (
            <>
              <div className="flex justify-between items-center mb-3 mt-6">
                <h2 className="typography-h3 text-primary font-medium">
                  {isComponentizationUser ? 'Choose Formulas' : 'Add to plan'}
                </h2>
              </div>
              {isMultiFormulaSelectUser ? (
                nonSubscribedProducts.map((formula: FormulaProduct) => (
                  <ProductCard
                    key={formula.product_id}
                    formula={formula}
                    isSelected={selectedProductIds.has(
                      String(formula.product_id),
                    )}
                    onToggleSelected={handleToggleProductSelected}
                    isEditable={true}
                    selectionMode="checkbox"
                    cardStyle="default"
                    showPrice={isComponentizationUser}
                    discountPercentage={discountPercentage}
                  />
                ))
              ) : isComponentizationUser ? (
                <RadioGroup
                  value={Array.from(selectedProductIds)[0] ?? ''}
                  onValueChange={handleSelectSingleProduct}
                >
                  {nonSubscribedProducts.map((formula: FormulaProduct) => (
                    <ProductCard
                      key={formula.product_id}
                      formula={formula}
                      isSelected={selectedProductIds.has(
                        String(formula.product_id),
                      )}
                      onToggleSelected={handleSelectSingleProduct}
                      isEditable={true}
                      selectionMode="radio"
                      cardStyle="default"
                      showPrice={isComponentizationUser}
                      discountPercentage={discountPercentage}
                    />
                  ))}
                </RadioGroup>
              ) : (
                nonSubscribedProducts.map((formula: FormulaProduct) => (
                  <ProductCard
                    key={formula.product_id}
                    formula={formula}
                    isSelected={true} // In this view, non-subscribed are shown as if to be added
                    isEditable={false}
                    cardStyle="default"
                  />
                ))
              )}
            </>
          )}
          {!isComponentizationUser && subscribedProducts.length > 0 && (
            <>
              <h2 className="typography-h3 text-primary font-medium mt-6 mb-4">
                Already in my plan
              </h2>
              {subscribedProducts.map((formula: FormulaProduct) => (
                <ProductCard
                  key={formula.product_id}
                  formula={formula}
                  isSelected={true}
                  isEditable={false}
                  cardStyle="subscribedHighlight"
                />
              ))}
            </>
          )}
        </div>
      </div>
    </>
  );
};

interface NotSubscribedViewProps {
  selectedFormulaCount: number;
  promotionData: { splitOne?: string; splitTwo?: string } | undefined;
  dynamicPricing: PriceInfo;
  ctaButtonLabel: string;
  selectedProductIds: Set<string>;
  nonSubscribedProducts: FormulaProduct[];
  products: FormulaProduct[]; // For fallback display logic
  handleCustomizeClick: () => void;
  isAnySubscribed: boolean;
  onCtaClick: () => void;
  isCtaLoading: boolean;
  isComponentizationUser: boolean;
  handleToggleProductSelected: (productId: string) => void;
  discountPercentage?: number;
}

export const NotSubscribedView = ({
  selectedFormulaCount,
  promotionData,
  dynamicPricing,
  ctaButtonLabel,
  selectedProductIds,
  nonSubscribedProducts,
  products,
  handleCustomizeClick,
  isAnySubscribed,
  onCtaClick,
  isCtaLoading,
  isComponentizationUser,
  handleToggleProductSelected,
  discountPercentage,
}: NotSubscribedViewProps) => {
  let productsToDisplay = nonSubscribedProducts;
  if (selectedProductIds.size > 0) {
    productsToDisplay = nonSubscribedProducts.filter((p) =>
      selectedProductIds.has(String(p.product_id)),
    );
  } else if (
    selectedProductIds.size === 0 &&
    nonSubscribedProducts.length === 0 &&
    products.length > 0
  ) {
    productsToDisplay = products;
  } else if (
    selectedProductIds.size === 0 &&
    nonSubscribedProducts.length > 0
  ) {
    productsToDisplay = nonSubscribedProducts;
  }

  const isSplitTwoOfferValid =
    promotionData &&
    promotionData?.splitTwo &&
    new Date(promotionData.splitTwo) > new Date();

  return (
    <>
      <div className="flex justify-center">
        <LatestResultsBanner className="mb-6" />
      </div>

      <div className="container mx-auto max-w-5xl flex flex-col md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] gap-4 md:gap-8 p-4 pb-20 md:pb-4">
        <div className="contents md:block md:min-w-0">
          <div className="order-1 flex items-start flex-col">
            <div className="flex flex-nowrap max-w-fit backdrop-blur-[25px] mb-3 empty:hidden">
              {promotionData?.splitOne && (
                <div
                  className={`typography-caption text-primary-foreground py-2 px-4 bg-gradient-to-r from-[#57D3BD] to-[#038F76] ${
                    isSplitTwoOfferValid ? 'rounded-l-full' : 'rounded-full'
                  }`}
                >
                  {promotionData.splitOne}
                </div>
              )}
              {isSplitTwoOfferValid && (
                <div className="typography-btn-medium flex items-center text-background-success py-2 px-4 rounded-r-full bg-gradient-to-r from-[#B1EAE0] to-[#A2E6D7]">
                  <OfferBannerWithTimer offerEndDate={promotionData.splitTwo} />
                </div>
              )}
            </div>
            <h1 className="typography-h1 text-primary mb-2">
              Precision Formulas
            </h1>
            <p className="typography-headline2 font-serif italic text-primary md:mb-4">
              Made-for-you
            </p>
            <div className="flex items-baseline gap-2 flex-wrap mt-2 mb-4">
              <FormulaPriceSummary priceInfo={dynamicPricing} />
            </div>
          </div>
          <div className="sticky bottom-16 order-3 -mx-4 flex flex-wrap items-center gap-3 md:static md:mx-0 md:block z-50 border-t border-gray-200 bg-white px-4 py-3 md:border-0 md:bg-transparent md:p-0">
            {isComponentizationUser && (
              <SelectedFormulaTotal
                priceInfo={dynamicPricing}
                count={selectedFormulaCount}
              />
            )}
            <Button
              className="min-h-12 flex-1 rounded-md py-3 bg-black text-white hover:bg-opacity-90 typography-btn-medium min-w-[140px] md:min-w-0 px-4 md:px-5"
              onClick={onCtaClick}
              disabled={isCtaLoading || !dynamicPricing.isPriceAvailable}
              data-testid="shopFormulas-orderButton"
            >
              {isCtaLoading ? 'Processing...' : ctaButtonLabel}
            </Button>
          </div>
        </div>
        <div className="order-2 w-full min-w-0">
          <div className="flex justify-between items-center mb-3">
            <h2 className="typography-h3 text-primary font-medium">
              {isComponentizationUser ? 'Choose Formulas' : 'Formulas included'}
            </h2>
            {!isComponentizationUser &&
              !isAnySubscribed &&
              nonSubscribedProducts.length > 0 && (
                <button
                  onClick={handleCustomizeClick}
                  className="flex items-center typography-body-sm text-[#038F76] hover:text-[#027B63]"
                >
                  Customize <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              )}
          </div>
          {isComponentizationUser
            ? nonSubscribedProducts.map((formula: FormulaProduct) => (
                <ProductCard
                  key={formula.product_id}
                  formula={formula}
                  isSelected={selectedProductIds.has(
                    String(formula.product_id),
                  )}
                  onToggleSelected={handleToggleProductSelected}
                  isEditable={true}
                  selectionMode="checkbox"
                  cardStyle="default"
                  showPrice={true}
                  discountPercentage={discountPercentage}
                />
              ))
            : productsToDisplay.map((formula: FormulaProduct) => (
                <ProductCard
                  key={formula.product_id}
                  formula={formula}
                  isSelected={
                    selectedProductIds.size > 0
                      ? selectedProductIds.has(String(formula.product_id))
                      : true
                  }
                  isEditable={false}
                  cardStyle="default"
                />
              ))}
        </div>
      </div>
    </>
  );
};
